import React from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import { darken } from '@mui/material';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import * as Sentry from '@sentry/react';

import { useToastError } from '@hooks';

export default function ErrorBoundary() {
  const theme = useTheme();
  const navigate = useNavigate();
  let error = useRouteError();
  const toastError = useToastError();

  React.useEffect(() => {
    let errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
    import.meta.env.DEV && console.error('[ErrorBoundary] Error:', errorMessage);

    if (error) {
      // Log dans Sentry
      Sentry.captureException(error instanceof Error ? error : new Error(JSON.stringify(error)));

      // Toast UI
      toastError(error);
    }
    return () => {
      error = null;
    };
  }, [error]);

  const handleNavigate = () => {
    navigate('/', { replace: true });
  };

  return (
    <Stack
      p={2}
      width="100%"
      height="100vh"
      spacing={3}
      alignItems="center"
      justifyContent="center"
      sx={{
        borderRadius: theme.borderRadius,
        boxShadow: theme.boxShadow,
      }}
    >
      <Typography color="primary" variant="h3">
        OOps
      </Typography>
      <Typography variant="h6" color="textSecondary">
        Quelque chose s'est mal passé...
      </Typography>
      {import.meta.env.DEV && (
        <Stack
          p={2}
          bgcolor={darken(theme.palette.error.main, 0.5)}
          borderRadius={1}
          boxShadow={3}
          sx={{
            border: `1px solid ${theme.palette.error.main}`,
          }}
        >
          <Typography variant="body2" color="textPrimary">
            {error instanceof Error ? error.message : 'Une erreur inconnue est survenue.'}
          </Typography>
        </Stack>
      )}
      <Divider variant="middle" />
      <Button variant="contained" onClick={handleNavigate}>
        Retour à la page d'accueil
      </Button>
    </Stack>
  );
}
